import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Activity, Loader2, Plus, Trash2, CheckCircle2, Circle, Server, Database, TrendingUp, Layers as LayersIcon } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api } from "../../lib/api";
import { useI18n } from "../../context/I18nContext";

const ease = [0.16, 1, 0.3, 1];

function Sparkline({ series, testid }) {
  const max = Math.max(1, ...series.map((s) => s.count));
  return (
    <div className="flex items-end gap-0.5 h-10" data-testid={testid}>
      {series.map((s, i) => (
        <div key={i} className="flex-1 bg-violet/70 rounded-sm min-h-[2px]" style={{ height: `${(s.count / max) * 100}%` }} title={`${s.date}: ${s.count}`} />
      ))}
    </div>
  );
}

export default function Plataforma() {
  const { t } = useI18n();
  const [status, setStatus] = useState(null);
  const [health, setHealth] = useState(null);
  const [roadmap, setRoadmap] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kpiForm, setKpiForm] = useState({ title: "", event_filter: "" });
  const [addingKpi, setAddingKpi] = useState(false);
  const [sears, setSears] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const loadSears = useCallback(async () => {
    try { setSears((await api.get("/platform/seed-sears/status")).data); } catch { /* no super admin */ }
  }, []);

  const loadKpis = useCallback(async () => { setKpis((await api.get("/platform/kpis")).data); }, []);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, h, r] = await Promise.all([api.get("/platform/status"), api.get("/platform/health"), api.get("/platform/roadmap")]);
      setStatus(s.data); setHealth(h.data); setRoadmap(r.data);
      await loadKpis();
    } catch { /* */ } finally { setLoading(false); }
  }, [loadKpis]);
  useEffect(() => { load(); loadSears(); }, [load, loadSears]);
  useEffect(() => {
    if (!sears?.running) return;
    const iv = setInterval(loadSears, 4000);
    return () => clearInterval(iv);
  }, [sears?.running, loadSears]);

  const runSears = async () => {
    if (!window.confirm("Esto borra y regenera TODO el entorno demo SEARS (tenant 'sears'). ¿Continuar?")) return;
    setSeeding(true);
    try { await api.post("/platform/seed-sears"); toast.success("Aprovisionamiento SEARS iniciado"); setTimeout(loadSears, 1500); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSeeding(false); }
  };

  const addKpi = async () => {
    if (!kpiForm.title.trim()) return;
    setAddingKpi(true);
    try { await api.post("/platform/kpis", kpiForm); toast.success(t("os.platform.kpiCreated")); setKpiForm({ title: "", event_filter: "" }); loadKpis(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setAddingKpi(false); }
  };
  const delKpi = async (id) => { await api.delete(`/platform/kpis/${id}`); loadKpis(); };

  if (loading || !status) return <OSLayout title="Plataforma"><div className="py-24 flex justify-center text-navy/40"><Loader2 className="w-7 h-7 animate-spin" /></div></OSLayout>;

  const statusLabel = { implemented: t("os.platform.implemented"), planned: t("os.platform.planned"), exploring: t("os.platform.exploring") };

  return (
    <OSLayout title="Plataforma">
      <div className="space-y-8" data-testid="plataforma-page">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
          <div className="overline text-navy/40 mb-2 flex items-center gap-2"><Activity className="w-3.5 h-3.5" /> {t("os.platform.overline")}</div>
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="font-editorial text-4xl md:text-5xl tracking-tight">{t("os.platform.title")}</h2>
            <span className="badge badge-violet" data-testid="platform-scope">{status.scope === "global" ? t("os.platform.global") : t("os.platform.tenant")}</span>
          </div>
          <p className="mt-2 text-navy/50 max-w-2xl">{t("os.platform.subtitle")}</p>
        </motion.div>

        {/* Counts */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="platform-counts">
          {Object.entries(status.counts).map(([k, v]) => (
            <div key={k} className="card-surface p-4">
              <div className="font-editorial text-3xl">{v}</div>
              <div className="text-xs text-navy/40 mt-1 capitalize">{k.replace("_", " ")}</div>
            </div>
          ))}
        </div>

        {/* Entorno demo SEARS (solo super admin) */}
        {status.scope === "global" && sears && (
          <div className="card-surface p-6" data-testid="platform-sears-seed">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <h3 className="font-medium flex items-center gap-2"><Database className="w-4 h-4 text-violet" /> Entorno demo SEARS</h3>
                <p className="text-xs text-navy/50 mt-1 max-w-xl">90 tiendas · 1,500 empleados · 4,000 SKUs · inventario, compras, finanzas, campañas y 7 cuentas demo (contraseña Sears2026). Regenerarlo borra y recrea todo el tenant «sears».</p>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {Object.entries(sears.counts || {}).map(([k, v]) => (
                    <span key={k} className="badge badge-muted" data-testid={`sears-count-${k}`}>{k} · {v.toLocaleString()}</span>
                  ))}
                </div>
                {sears.error && <p className="text-xs text-destructive mt-2" data-testid="sears-seed-error">{sears.error}</p>}
              </div>
              <button onClick={runSears} disabled={seeding || sears.running} className="btn btn-primary text-sm gap-2 shrink-0" data-testid="sears-seed-btn">
                {(seeding || sears.running) ? <><Loader2 className="w-4 h-4 animate-spin" /> Generando…</> : "Regenerar entorno SEARS"}
              </button>
            </div>
          </div>
        )}

        {/* Layer map */}
        <div className="card-surface p-6" data-testid="platform-layers">
          <h3 className="font-medium mb-4 flex items-center gap-2"><LayersIcon className="w-4 h-4 text-violet" /> {t("os.platform.layersMap")}</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {status.layers.map((l) => (
              <div key={l.id} className="flex items-center gap-2.5 p-2.5 rounded-lg bg-navy/[0.03]" data-testid={`layer-${l.id}`}>
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <span className="font-mono text-xs text-violet">{l.id}</span>
                <span className="text-sm truncate">{l.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Health */}
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 card-surface p-6" data-testid="platform-activity">
            <h3 className="font-medium mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-violet" /> {t("os.platform.activity")} · {health.events_total} {t("os.platform.events")}</h3>
            <Sparkline series={health.activity_14d} testid="activity-sparkline" />
            <div className="mt-5 text-xs text-navy/40 mb-2">{t("os.platform.byDomain")}</div>
            <div className="flex flex-wrap gap-1.5">
              {health.by_domain.map((d) => <span key={d.domain} className="badge badge-muted">{d.domain} · {d.count}</span>)}
            </div>
          </div>
          <div className="card-surface p-6" data-testid="platform-services">
            <h3 className="font-medium mb-4 flex items-center gap-2"><Server className="w-4 h-4 text-violet" /> {t("os.platform.services")}</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm"><span className="flex items-center gap-2"><Server className="w-4 h-4 text-navy/40" /> API</span><span className={`badge ${health.services.api === "ok" ? "badge-emerald" : "badge-muted"}`}>{health.services.api}</span></div>
              <div className="flex items-center justify-between text-sm"><span className="flex items-center gap-2"><Database className="w-4 h-4 text-navy/40" /> Database</span><span className={`badge ${health.services.database === "ok" ? "badge-emerald" : "badge-muted"}`}>{health.services.database}</span></div>
            </div>
          </div>
        </div>

        {/* Custom KPIs */}
        <div className="card-surface p-6" data-testid="platform-kpis">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <h3 className="font-medium flex items-center gap-2"><TrendingUp className="w-4 h-4 text-violet" /> {t("os.platform.kpis")}</h3>
            <div className="flex gap-2 items-center">
              <input className="field-sm" placeholder={t("os.platform.kpiTitle")} value={kpiForm.title} onChange={(e) => setKpiForm({ ...kpiForm, title: e.target.value })} data-testid="kpi-title" />
              <input className="field-sm" placeholder={t("os.platform.kpiFilter")} value={kpiForm.event_filter} onChange={(e) => setKpiForm({ ...kpiForm, event_filter: e.target.value })} data-testid="kpi-filter" />
              <button onClick={addKpi} disabled={addingKpi} className="btn btn-primary text-sm" data-testid="kpi-add">
                {addingKpi ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              </button>
            </div>
          </div>
          {kpis.length === 0 ? <p className="text-sm text-navy/40">{t("os.platform.noKpis")}</p>
            : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {kpis.map((k) => (
                  <div key={k.id} className="p-4 rounded-lg bg-navy/[0.03]" data-testid={`kpi-${k.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="font-editorial text-3xl">{k.value}</div>
                      <button onClick={() => delKpi(k.id)} className="text-navy/30 hover:text-destructive" data-testid={`kpi-del-${k.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                    <div className="text-sm mt-1">{k.title}</div>
                    {k.event_filter && <div className="text-[11px] text-navy/40 font-mono">{k.event_filter}</div>}
                    <div className="mt-2"><Sparkline series={k.series} /></div>
                  </div>
                ))}
              </div>
            )}
        </div>

        {/* Roadmap */}
        <div className="card-surface p-6" data-testid="platform-roadmap">
          <h3 className="font-medium mb-4 flex items-center gap-2"><Circle className="w-4 h-4 text-violet" /> {t("os.platform.roadmap")}</h3>
          <div className="space-y-2">
            {roadmap.map((r) => (
              <div key={r.id} className="flex items-start gap-3 p-3 rounded-lg bg-navy/[0.03]" data-testid={`roadmap-${r.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm">{r.title}</div>
                  <div className="text-xs text-navy/50 mt-0.5">{r.detail}</div>
                </div>
                <span className={`badge shrink-0 ${r.status === "exploring" ? "badge-violet" : "badge-muted"}`}>{statusLabel[r.status] || r.status}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </OSLayout>
  );
}
